import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { generateId } from '../../utility';
import { useMergedRef } from '../../hooks/useMergedRef';
import type { TargetedEvent } from 'preact';
import type { Option } from '../form-field';
import type { HTMLAttributes, PickRequired } from '../../types/utility';
import { UnorderedList } from '../list';

interface NavigationProps {
  allowPartialMatch?: boolean;
  items: PickRequired<Option, 'href'>[];
  'aria-label': string;
  /**
   * @deprecated Use `items` instead
   */
  options?: PickRequired<Option, 'href'>[];
  orientation?: 'horizontal' | 'vertical';
  type?: 'bounded' | 'unbounded' | 'unbounded-compact';
}

type Props = NavigationProps & HTMLAttributes<HTMLElement>;

export default function Navigation({
  allowPartialMatch = true,
  className,
  items,
  options,
  orientation = 'vertical',
  // React 19 delivers `ref` as a regular prop; keep it out of the spread so
  // it can't clobber our internal ref
  ref: forwardedRef,
  type = 'bounded',
  ...props
}: Props) {
  const id = useMemo(generateId, []);
  const classList = cn('navigation', className, {
    [orientation]: orientation !== 'vertical',
    [type]: type !== 'bounded',
  });

  const navRef = useRef<HTMLElement>(null);
  const mergedNavRef = useMergedRef(navRef, forwardedRef);

  const [currentPathname, setCurrentPath] = useState(location?.pathname);

  const handleNavigate = useCallback(
    (e: TargetedEvent<Window> | TargetedEvent<HTMLElement>) => {
      const { target } = e;
      let newPathname;

      if (target === window) {
        newPathname = location.pathname;
      } else {
        newPathname = (target as HTMLElement)?.closest('a')?.pathname;
      }

      if (newPathname && currentPathname !== newPathname) {
        setCurrentPath(newPathname);
      }
    },
    [currentPathname],
  );

  useEffect(() => {
    document.addEventListener('click', handleNavigate as EventListener);
    window.addEventListener('popstate', handleNavigate as EventListener);

    return () => {
      document.removeEventListener('click', handleNavigate as EventListener);
      window.removeEventListener('popstate', handleNavigate as EventListener);
    };
  }, [handleNavigate]);

  // edge case: ensure activeElement that isn't current loses focus to ensure :focus styling isn't applied (surfaces with back/forward navigation)
  useEffect(() => {
    const { activeElement } = document;
    if (
      activeElement &&
      !activeElement.getAttribute('aria-current') &&
      navRef.current?.contains(activeElement)
    ) {
      (activeElement as HTMLAnchorElement).blur();
    }
  }, [currentPathname]);

  return (
    <nav ref={mergedNavRef} className={classList} {...props}>
      <ul>
        {(items || options || []).map(({ label, href, subtext, ...rest }, index) => {
          const descId = `navigation-${id}-${index}-desc`;
          let isCurrentPage;

          if (allowPartialMatch && href !== '/') {
            isCurrentPage = currentPathname.startsWith(href);
          } else {
            isCurrentPage = currentPathname === href;
          }

          let description;
          if (typeof subtext === 'string') {
            description = (
              <small aria-hidden="true" id={descId}>
                {subtext}
              </small>
            );
          } else if (Array.isArray(subtext)) {
            description = (
              <UnorderedList
                aria-hidden="true"
                className="small"
                id={descId}
                gap="x-small"
                items={subtext}
                marker={{ type: 'string', value: '•' }}
                orientation="horizontal"
              />
            );
          } else if (subtext) {
            description = (
              <div aria-hidden="true" id={descId} className="small">
                {subtext}
              </div>
            );
          }

          return (
            <li key={label}>
              <a
                href={href}
                aria-current={isCurrentPage ? 'page' : undefined}
                aria-describedby={descId}
                data-icon={type.startsWith('bounded')}
                {...rest}
              >
                {label}
                {description}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
